import type { Permission } from '@mt/shared';
import {
  BookMarked,
  BookOpenCheck,
  ShieldAlert,
  ImagePlay,
  FileAudio,
  FerrisWheel,
  CarFront,
  ClipboardCheck,
  ClipboardList,
  Clock,
  FileText,
  HandCoins,
  IdCard,
  ListTree,
  Fuel,
  Hammer,
  HardHat,
  ListTodo,
  Map as MapIcon,
  MapPinned,
  MapPinCheck,
  MessagesSquare,
  Newspaper,
  Package,
  PackageCheck,
  Route,
  ShieldCheck,
  Stamp,
  Signature,
  Truck,
  UserRound,
  Users,
  Warehouse,
  Zap,
  BookImage,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { openInboxList } from '@/features/zalo/inbox-bridge';
import { menuAllowed } from '@/lib/menu-access';

export interface Tile {
  label: string;
  hint: string;
  icon: LucideIcon;
  to?: string;
  action?: () => void;
  perm?: Permission;
  anyPerm?: Permission[];
  /** Chỉ cho nhân viên công ty: ẩn với tài khoản chỉ là CTV (DEC-268). */
  staffOnly?: boolean;
  /** Chỉ cho tài khoản chỉ là CTV (DEC-375). */
  ctvOnly?: boolean;
}
export interface Section {
  key: string;
  title: string;
  tiles: Tile[];
}

export const LAUNCHER_SECTIONS: Section[] = [
  {
    key: 'nv',
    title: 'Cổng nhân viên',
    tiles: [
      { label: 'Chấm công', hint: 'Chụp ảnh, lấy vị trí', icon: Clock, to: '/cham-cong', staffOnly: true },
      { label: 'Xin nghỉ, đi trễ', hint: 'Đơn từ, phiếu lương', icon: UserRound, to: '/cong-nhan-vien', staffOnly: true },
      { label: 'Việc của tôi', hint: 'Tăng ca, điều động, báo cáo', icon: ClipboardList, to: '/cong-nhan-vien?tab=viec', staffOnly: true },
      { label: 'Bảng tin', hint: 'Ảnh, thông báo, sinh nhật', icon: Newspaper, to: '/bang-tin', perm: 'feed.use' },
      { label: 'Vòng quay may mắn', hint: 'Uống bia, trúng thưởng, quà', icon: FerrisWheel, to: '/bang-tin/vong-quay', perm: 'feed.use' },
    ],
  },
  {
    key: 'tx',
    title: 'Tài xế',
    tiles: [
      { label: 'Giao hàng, nhận xe', hint: 'Giao, ký nhận, đổ dầu', icon: Fuel, to: '/tai-xe', perm: 'fleet.driver' },
    ],
  },
  {
    key: 'sale',
    title: 'Bán hàng',
    tiles: [
      { label: 'Báo giá nhanh', hint: 'Dán ảnh, gửi khách', icon: Zap, to: '/bao-gia-nhanh', perm: 'quote.create' },
      { label: 'Hộp thư', hint: 'Zalo, Messenger, email', icon: MessagesSquare, action: openInboxList, perm: 'zalo.use' },
      // DMS (DEC-610): NV thị trường đi tuyến, check-in tại điểm
      { label: 'Viếng thăm', hint: 'Tuyến hôm nay, check-in', icon: MapPinCheck, to: '/vieng-tham', perm: 'visit.use' },
      { label: 'Việc cần làm', hint: 'Gọi lại, chăm sóc', icon: ListTodo, to: '/viec-can-lam', perm: 'customer.read' },
      { label: 'Khách hàng', hint: 'Tra cứu, liên hệ', icon: Users, to: '/khach-hang', perm: 'customer.read' },
      { label: 'Báo giá', hint: 'Danh sách, trạng thái', icon: FileText, to: '/bao-gia', perm: 'quote.read' },
      // CTV dùng ô Đơn hàng của tôi bên dưới (DEC-375)
      { label: 'Đơn hàng', hint: 'Đơn của tôi, tình trạng giao', icon: PackageCheck, to: '/don-hang', perm: 'sales_order.read', staffOnly: true },
      // DEC-375: CTV
      { label: 'Đơn hàng của tôi', hint: 'Đã chốt, đang giao, đã giao', icon: PackageCheck, to: '/don-cua-toi', perm: 'quote.read', ctvOnly: true },
      { label: 'Doanh số & hoa hồng', hint: 'Theo tháng, tạm tính', icon: HandCoins, to: '/thu-nhap', perm: 'quote.read', ctvOnly: true },
      { label: 'Sản phẩm', hint: 'Tên, quy cách, sẵn sàng', icon: Package, to: '/san-pham', perm: 'product.read', ctvOnly: true },
      { label: 'Giao hàng', hint: 'Phiếu giao, tình trạng', icon: MapPinned, to: '/giao-hang', perm: 'delivery.read' },
    ],
  },
  {
    // Điện thoại gọn (anh Tiến 14/10/2026): việc chính của kho, đội trưởng có ô riêng, không phải vào Menu
    key: 'kho',
    title: 'Kho',
    tiles: [
      { label: 'Soạn hàng', hint: 'Lấy hàng theo phiếu giao', icon: PackageCheck, to: '/soan-hang', perm: 'delivery.pick' },
      { label: 'Phiếu kho', hint: 'Nhập, xuất, chuyển kho', icon: ClipboardList, to: '/phieu-kho', perm: 'stock_doc.read' },
      { label: 'Tồn kho', hint: 'Số tồn theo từng kho', icon: Warehouse, to: '/ton-kho', perm: 'stock_doc.read' },
      { label: 'Kiểm kho', hint: 'Đếm hàng theo kho', icon: ClipboardCheck, to: '/kiem-kho', perm: 'stock_doc.read' },
    ],
  },
  {
    // Điều phối (anh Tiến 14/10/2026): bảng điều phối, bản đồ xe, chuyến lấy hàng ra màn chính
    key: 'dieu-phoi',
    title: 'Điều phối',
    tiles: [
      { label: 'Điều phối', hint: 'Xếp phiếu giao lên xe', icon: Route, to: '/van-chuyen', perm: 'delivery.read_all' },
      { label: 'Bản đồ xe', hint: 'Xe đang ở đâu, chuyến hôm nay', icon: MapIcon, to: '/van-chuyen?tab=ban-do', perm: 'delivery.read_all' },
      { label: 'Chuyến lấy hàng', hint: 'Lấy hàng ở NCC, kho ngoài', icon: Truck, to: '/doi-xe?tab=chuyen-xe', perm: 'fleet.read' },
      // DEC-412: NV tự đặt Lalamove ngay trên ứng dụng (anh Tiến 21/10/2026)
      { label: 'Đặt xe Lalamove', hint: 'Xe ngoài của tôi, chờ duyệt', icon: CarFront, to: '/xe-ngoai', anyPerm: ['carrier.self_book', 'carrier.approve', 'carrier.manage', 'carrier.wallet'] },
    ],
  },
  {
    // DEC-572: nhóm "Dự án thi công" (Đội khoán cũ)
    key: 'thi-cong',
    title: 'Dự án SX-TC',
    tiles: [
      { label: 'Báo cáo công trình', hint: 'Tiến độ, báo cáo ngày hiện trường', icon: Hammer, to: '/thi-cong?tab=tien-do', anyPerm: ['hr.piece.manage', 'hr.piece.accept', 'hr.piece.approve'] },
      // DEC-573: người phụ trách bước báo cáo ngày trên điện thoại
      { label: 'Giai đoạn của tôi', hint: 'Bước tôi phụ trách, báo cáo ngày', icon: ListTree, to: '/thi-cong?tab=giai-doan', anyPerm: ['hr.piece.manage', 'hr.piece.accept', 'hr.piece.approve'] },
      { label: 'Hồ sơ công nhân', hint: 'Chụp CCCD, hợp đồng, an toàn', icon: IdCard, to: '/thi-cong?tab=ho-so-cn', anyPerm: ['hr.piece.manage', 'hr.piece.accept', 'hr.piece.approve'] },
      { label: 'Đợt thanh toán', hint: 'Nghiệm thu, tạm ứng đội', icon: HandCoins, to: '/thi-cong', anyPerm: ['hr.piece.manage', 'hr.piece.accept', 'hr.piece.approve'] },
    ],
  },
  {
    // CR-016: CTV, NV KD, tài xế báo công trình đang thi công; KD thị trường xem bản đồ
    key: 'du-an',
    title: 'Dự án công trình',
    tiles: [
      { label: 'Báo dự án', hint: 'Chụp công trình, gửi vị trí', icon: HardHat, to: '/du-an/bao', perm: 'site.report' },
      { label: 'Bản đồ dự án', hint: 'Nhận tiếp cận', icon: MapIcon, to: '/du-an', perm: 'site.manage' },
    ],
  },
  {
    // Menu Tài liệu doanh nghiệp (DEC-486): mọi NV đọc quy chế, sổ tay, tài liệu an toàn trên điện thoại
    key: 'tai-lieu',
    title: 'Tài liệu',
    tiles: [
      { label: 'Quy chế', hint: 'Nội quy, quy định, quy trình', icon: BookMarked, to: '/quy-che', staffOnly: true },
      { label: 'Sổ tay vận hành', hint: 'Hướng dẫn máy, thiết bị, phần mềm', icon: BookOpenCheck, to: '/so-tay-van-hanh', staffOnly: true },
      { label: 'Tài liệu an toàn', hint: 'ATLĐ, an toàn điện, PCCC', icon: ShieldAlert, to: '/tai-lieu-an-toan', staffOnly: true },
      { label: 'Hình ảnh', hint: 'Ảnh sản phẩm, ảnh thi công', icon: ImagePlay, to: '/hinh-anh', perm: 'media.read' },
      { label: 'Ghi âm, video', hint: 'Kịch bản chăm sóc, video hướng dẫn', icon: FileAudio, to: '/ghi-am-video', perm: 'media.read' },
      { label: 'Thư viện bán hàng', hint: 'Ảnh QC, bài Zalo, video đã làm', icon: BookImage, to: '/thu-vien-ban-hang', perm: 'poster.use' },
    ],
  },
  {
    key: 'duyet',
    title: 'Chờ tôi duyệt',
    tiles: [
      // P23 (DEC-458): Hộp duyệt chung – duyệt trên điện thoại, NV gửi tờ trình
      { label: 'Hộp duyệt', hint: 'Tờ trình, phiếu chờ tôi duyệt', icon: Stamp, to: '/duyet', staffOnly: true },
      { label: 'Ký điện tử', hint: 'Biên bản, hợp đồng chờ tôi ký', icon: Signature, to: '/ky-dien-tu', staffOnly: true },
      { label: 'Duyệt giá', hint: 'Báo giá dưới giá sàn', icon: ShieldCheck, to: '/duyet-gia', perm: 'quote.approve' },
      { label: 'Duyệt đơn', hint: 'Nghỉ, đi trễ, tạm ứng', icon: ClipboardCheck, to: '/duyet-don', anyPerm: ['hr.leave.approve', 'hr.advance.check', 'hr.advance.approve'] },
      { label: 'Giao việc', hint: 'Tăng ca, Chủ nhật, điều động', icon: ClipboardList, to: '/giao-viec', anyPerm: ['hr.leave.approve', 'hr.employee.manage'] },
      { label: 'Duyệt % hoa hồng CTV', hint: 'CTV đề xuất theo đơn', icon: HandCoins, to: '/duyet-hoa-hong-ctv', perm: 'ctv_reward.manage' },
    ],
  },
];

/** Ô màn mở đầu người dùng được thấy, lọc theo quyền như menu (dùng chung cho LauncherPage và thanh dưới AppShell). */
export function visibleSections(can: (p: Permission) => boolean, ctvOnly: boolean): Section[] {
  const allowed = (t: Tile) => !(t.staffOnly && ctvOnly) && !(t.ctvOnly && !ctvOnly) && (t.anyPerm ? t.anyPerm.some((p) => can(p)) : !t.perm || can(t.perm)) && (!t.to || menuAllowed(can, t.to));
  return LAUNCHER_SECTIONS.map((s) => ({
    ...s,
    // CTV không phải nhân viên công ty: nhóm chỉ còn Bảng tin thì không gọi là Cổng nhân viên (DEC-375)
    title: ctvOnly && s.key === 'nv' ? 'Công ty' : s.title,
    tiles: s.tiles.filter(allowed),
  })).filter((s) => s.tiles.length);
}
